import { initializeApp, FirebaseApp } from 'firebase/app'
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  Auth,
  connectAuthEmulator,
  signOut,
  onAuthStateChanged,
  User,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  OAuthProvider,
  FacebookAuthProvider,
  AuthProvider,
  AuthCredential,
  linkWithCredential,
  updateProfile,
} from 'firebase/auth'

let firebaseApp: FirebaseApp | null = null
let auth: Auth | null = null
let configError: Error | null = null

// Local-only Auth Emulator mode. Only honored when explicitly enabled AND the
// build is not a production build — an emulator host accepts unverified
// tokens, so it must never be reachable from a prod bundle regardless of
// what env vars happen to be set at build time.
const useEmulator =
  import.meta.env.VITE_USE_FIREBASE_AUTH_EMULATOR === 'true' && import.meta.env.MODE !== 'production'

// Try to initialize Firebase if config is available
try {
  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  }

  // Check if all required config values are present
  const missingKeys = Object.entries(firebaseConfig)
    .filter(([_, value]) => !value)
    .map(([key]) => key)

  if (missingKeys.length > 0) {
    configError = new Error(
      `Firebase configuration incomplete. Missing: ${missingKeys.join(', ')}. ` +
      `See .env.example for required environment variables.`
    )
  } else {
    firebaseApp = initializeApp(firebaseConfig)
    auth = getAuth(firebaseApp)

    if (useEmulator) {
      const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST || '127.0.0.1:9099'
      connectAuthEmulator(auth, `http://${emulatorHost}`, { disableWarnings: true })
    }
  }
} catch (error) {
  configError = error instanceof Error ? error : new Error(String(error))
}

export { useEmulator }

export { configError }

export function getAuthInstance(): Auth {
  if (!auth) {
    throw configError || new Error('Firebase Auth not initialized')
  }
  return auth
}

// handoff §4: four federated providers, in this order. Each needs enabling in
// the Firebase console (plus its OAuth redirect URI) before it works against a
// real project, so Microsoft, Apple and Meta only appear when
// VITE_FIREBASE_MULTI_PROVIDER is 'true' (DECISIONS #2). The emulator accepts
// all four.
export type ProviderId = 'google' | 'microsoft' | 'apple' | 'meta'

export const multiProviderEnabled = import.meta.env.VITE_FIREBASE_MULTI_PROVIDER === 'true'

// Meta is off: Meta will not publish the app without a verified business portfolio, which we cannot create. The
// code stays; put 'meta' back here (and the Crew button) once the Meta app is live.
export const enabledProviders: ProviderId[] = multiProviderEnabled
  ? ['google', 'microsoft', 'apple']
  : ['google']

function authProviderFor(id: ProviderId): AuthProvider {
  switch (id) {
    case 'google':
      return new GoogleAuthProvider()
    case 'microsoft': {
      const p = new OAuthProvider('microsoft.com')
      p.addScope('email')
      return p
    }
    case 'apple': {
      const p = new OAuthProvider('apple.com')
      p.addScope('email')
      return p
    }
    case 'meta': {
      const p = new FacebookAuthProvider()
      p.addScope('email')
      return p
    }
  }
}

const PROVIDER_NAMES: Record<ProviderId, string> = { google: 'Google', microsoft: 'Microsoft', apple: 'Apple', meta: 'Meta' }

function credentialFromError(id: ProviderId, error: unknown): AuthCredential | null {
  const e = error as Parameters<typeof OAuthProvider.credentialFromError>[0]
  switch (id) {
    case 'google': return GoogleAuthProvider.credentialFromError(e)
    case 'meta': return FacebookAuthProvider.credentialFromError(e)
    default: return OAuthProvider.credentialFromError(e)
  }
}

// The address on the error, or (with e-mail enumeration protection on, Firebase leaves it off) the one in the
// provider's ID token. Meta gives no ID token, so its address can be unknown.
function emailOf(error: unknown, credential: AuthCredential | null): string | null {
  const fromError = (error as { customData?: { email?: string } })?.customData?.email
  if (fromError) return fromError.toLowerCase()
  const idToken = (credential as { idToken?: string } | null)?.idToken
  if (!idToken) return null
  try {
    const b64 = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=')))
    const email = claims.email || claims.preferred_username
    return typeof email === 'string' && email.includes('@') ? email.toLowerCase() : null
  } catch {
    return null
  }
}

// The project keeps one account per e-mail address. A person who first signed in with one button and now presses
// another gets auth/account-exists-with-different-credential. We keep that second credential and ask them to use
// their first button once; when that succeeds (for the same address, where known, and within ten minutes), the
// second is linked to the account, so either button signs in from then on and their CONTROL membership (keyed by
// uid) is unchanged.
const LINK_WINDOW_MS = 10 * 60 * 1000
let pendingLink: { credential: AuthCredential; email: string | null; provider: ProviderId; at: number } | null = null

export async function signInWithProvider(id: ProviderId): Promise<void> {
  const authInstance = getAuthInstance()
  try {
    const result = await signInWithPopup(authInstance, authProviderFor(id))
    const pending = pendingLink
    pendingLink = null
    const sameAddress = !pending?.email || result.user.email?.toLowerCase() === pending.email
    if (pending && pending.provider !== id && sameAddress && Date.now() - pending.at < LINK_WINDOW_MS) {
      // Best effort: a link that fails leaves the person signed in with the button that worked.
      await linkWithCredential(result.user, pending.credential).catch(() => {})
    }
  } catch (error) {
    const code = (error as { code?: string })?.code
    if (code !== 'auth/account-exists-with-different-credential') throw error
    const credential = credentialFromError(id, error)
    const email = emailOf(error, credential)
    if (credential) pendingLink = { credential, email, provider: id, at: Date.now() }
    const who = email ?? 'Your e-mail address'
    throw Object.assign(
      new Error(
        credential
          ? `${who} already signs in with a different button. Use that one once and ${PROVIDER_NAMES[id]} will be added, so either works from then on.`
          : `${who} already signs in with a different button. Please use that one.`,
      ),
      { code },
    )
  }
}

// Dataphyll Crew in Safari (pages/CrewWebPage.tsx) keeps its own sign-in, in localStorage. iPhone browsers
// can leave IndexedDB, where the default sign-in lives, hanging; Firebase then never finishes starting, so
// it never says who is signed in and a sign-in never leaves the page. The whole page goes to the provider
// and back, which an iPhone handles better than a popup; the sign-in pages are on this site's own domain
// (deploy-web.mjs), so Safari keeps the result.
let crewAuth: Auth | null = null
function getCrewAuth(): Auth {
  if (!crewAuth) {
    const app = initializeApp(getAuthInstance().app.options, 'crew-web')
    crewAuth = initializeAuth(app, { persistence: browserLocalPersistence, popupRedirectResolver: browserPopupRedirectResolver })
    if (useEmulator) {
      const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST || '127.0.0.1:9099'
      connectAuthEmulator(crewAuth, `http://${emulatorHost}`, { disableWarnings: true })
    }
  }
  return crewAuth
}

export function crewSignInRedirect(id: ProviderId): Promise<void> {
  return signInWithRedirect(getCrewAuth(), authProviderFor(id))
}

export function onCrewAuthStateChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(getCrewAuth(), callback)
}

export function crewSignOut(): Promise<void> {
  return signOut(getCrewAuth())
}

// The worker's first and last name, kept on the sign-in so the ID token carries it to the platform, which
// makes nobody without one. Apple sends a name only on the first sign-in, and the web flow keeps none.
export async function crewSetName(user: User, name: string): Promise<void> {
  await updateProfile(user, { displayName: name })
  await user.getIdToken(true)
}

export function signOutUser(): Promise<void> {
  const authInstance = getAuthInstance()
  return signOut(authInstance)
}

export function onAuthStateChange(
  callback: (user: User | null) => void
): (() => void) {
  const authInstance = getAuthInstance()
  return onAuthStateChanged(authInstance, callback)
}
